/**
 * generic.ts
 *
 * Generic programming utilities
 */

/**
 * Creates and returns the tuple [t1, t2]
 *
 * TODO(gewesp): Type return value as Readonly<>
 */
export function tuple<T1, T2>(t1: T1, t2: T2): [T1, T2] {
  return [t1, t2];
}

/**
 * Creates and returns the triple [t1, t2, t3]
 */
export function triple<T1, T2, T3>(t1: T1, t2: T2, t3: T3): [T1, T2, T3] {
  return [t1, t2, t3];
}

/**
 * Applies func to all key/value pairs in obj, type safe.
 *
 * Use this instead of Object.entries(obj).forEach(...) in case
 * obj is a Record with key type limited to a subtype of string.
 */
export function forAllEntries<Key extends string, Value>(
  obj: Record<Key, Value>,
  func: ([key, value]: [Key, Value]) => void
): void {
  Object.entries(obj).forEach(([key, value]: [string, unknown]) =>
    func([key as Key, value as Value])
  );
}

/**
 * Applies func to all key/value pairs in obj, type safe.
 *
 * Returns the array of all result values.
 *
 * Use this instead of Object.entries(obj).map(...) in case
 * obj is a Record with key type limited to a subtype of string.
 */
export function mapAllEntries<Key extends string, Value, Return>(
  obj: Record<Key, Value>,
  func: ([key, value]: [Key, Value]) => Return
): Return[] {
  return Object.entries(obj).map(([key, value]: [string, unknown]) =>
    func([key as Key, value as Value])
  );
}

/**
 * Convert the variant type T | T[] | undefined to consistent typing to avoid
 * myriads of special cases.
 *
 * - Undefined results in an empty array
 * - One single value in a one-element array
 * - Array is passed through
 */
export function asArray<T>(x: T | T[] | undefined): T[] {
  if (x === undefined) {
    return [];
  }

  if (Array.isArray(x)) {
    return x;
  }

  return [x];
}
